import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import GuestBanner from '../components/GuestBanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Heart,
  Dna,
  Microscope,
  FileText,
  ExternalLink,
  Dog,
  Shield,
  Activity,
  Stethoscope,
  Loader2,
  CheckCircle,
  AlertCircle,
  FlaskConical,
  GraduationCap,
  Users,
  Send,
  BookOpen,
  Globe,
  Building2,
  ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import DatabasesTab from '../components/DogHealth/DatabasesTab';
import ResearchGroupsTab from '../components/DogHealth/ResearchGroupsTab';
import DNATestingTab from '../components/DogHealth/DNATestingTab';
import OrganizationsTab from '../components/DogHealth/OrganizationsTab';
import BreedHealthInsightsPanel from '../components/BreedHealthInsightsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NHGRI_URL = 'https://research.nhgri.nih.gov/dog_genome/index.shtml';
const NHGRI_STUDIES_URL = 'https://research.nhgri.nih.gov/dog_genome/study_descriptions/index.shtml';
const NHGRI_FACEBOOK_URL = 'https://www.facebook.com/DogGenomeProject/';

const DogHealth = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [myDogs, setMyDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [selectedDog, setSelectedDog] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // DNA Submission form
  const [dnaForm, setDnaForm] = useState({
    dog_id: '',
    reason_for_submission: '',
    health_conditions: '',
    has_dna_test: false,
    dna_test_company: '',
    willing_to_share_results: true,
    veterinarian_name: '',
    veterinarian_clinic: '',
    veterinarian_email: '',
    additional_notes: '',
    consent_research: false,
    consent_contact: false
  });

  useEffect(() => {
    if (user) {
      fetchMyDogs();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchMyDogs = async () => {
    try {
      const response = await axios.get(`${API}/pets/me?pet_type=dog`);
      setMyDogs(response.data);
    } catch (error) {
      console.error('Error fetching dogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitDNA = async () => {
    if (!dnaForm.consent_research) {
      toast.error('Please consent to research participation');
      return;
    }
    
    setSubmitting(true);
    try {
      // Save submission record locally
      await axios.post(`${API}/health/dna-submissions`, {
        ...dnaForm,
        user_id: user.id,
        submitted_at: new Date().toISOString()
      });
      
      toast.success('Submission recorded! You will be redirected to NHGRI to complete the process.');
      setSubmitDialogOpen(false);
      
      // Open NHGRI studies page in new tab
      window.open(NHGRI_STUDIES_URL, '_blank');
    } catch (error) {
      // Even if API fails, guide user to NHGRI
      toast.info('Please visit NHGRI directly to submit your information');
      window.open(NHGRI_STUDIES_URL, '_blank');
    } finally {
      setSubmitting(false);
    }
  };

  const openDogSelection = (dog) => {
    setSelectedDog(dog);
    setDnaForm({
      ...dnaForm,
      dog_id: dog.id
    });
    setSubmitDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 to-pink-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <Heart className="w-10 h-10" />
              <h1 className="text-4xl font-heading font-medium">Your Dog's Health</h1>
            </div>
            <p className="text-white/90 text-lg mb-6">
              Contribute to groundbreaking canine genetics research while learning about your dog's health. 
              Partner with the NHGRI Dog Genome Project to help advance veterinary science.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button 
                className="bg-white text-red-600 hover:bg-white/90"
                onClick={() => window.open(NHGRI_URL, '_blank')}
              >
                <Dna className="w-4 h-4 mr-2" />
                Visit NHGRI Dog Genome Project
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
              {user && myDogs.length > 0 && (
                <Button 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/20"
                  onClick={() => setSubmitDialogOpen(true)}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Submit DNA Information
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Guest Banner */}
        <GuestBanner message="Sign up to submit DNA information and access your dog's health records" />
        
        {/* About NHGRI Section */}
        <section className="mb-12">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="border-2 border-red-100">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <Microscope className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <CardTitle className="font-heading">NHGRI Dog Genome Project</CardTitle>
                    <CardDescription>National Human Genome Research Institute</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  The NHGRI Dog Genome Project is a <strong>citizen science initiative</strong> run by 
                  Dr. Elaine Ostrander's Laboratory at NIH. It focuses on the genetics of health and 
                  body structure in domestic dogs.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <span className="text-sm">No experiments on dogs</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <span className="text-sm">Voluntary participation</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <span className="text-sm">Pet dogs only</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <span className="text-sm">Owner/vet submitted</span>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => window.open(NHGRI_URL, '_blank')}
                >
                  Learn More at NHGRI
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-100">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <FlaskConical className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="font-heading">Research Goals</CardTitle>
                    <CardDescription>Advancing canine and human health</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Research findings help understand genetic diseases in dogs AND provide insights 
                  into human health conditions. Over 350 inherited diseases have been identified in dogs.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Activity className="w-5 h-5 text-red-500" />
                    <div>
                      <p className="font-medium text-sm">Cancer Research</p>
                      <p className="text-xs text-muted-foreground">Locating genes involved in canine cancer</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Dog className="w-5 h-5 text-amber-500" />
                    <div>
                      <p className="font-medium text-sm">Morphologic Traits</p>
                      <p className="text-xs text-muted-foreground">Understanding body size, coat, and structure</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Shield className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="font-medium text-sm">Disease Prevention</p>
                      <p className="text-xs text-muted-foreground">Finding DNA variants to reduce disease</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* How to Participate */}
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-medium mb-6 flex items-center gap-3">
            <GraduationCap className="w-7 h-7 text-primary" />
            How to Participate
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">1</span>
                </div>
                <h3 className="font-heading text-lg mb-2">Review Studies</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Visit NHGRI's current project studies to see which research projects are actively 
                  seeking DNA samples from specific breeds or dogs with certain conditions.
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.open(NHGRI_STUDIES_URL, '_blank')}
                >
                  View Current Studies
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">2</span>
                </div>
                <h3 className="font-heading text-lg mb-2">Prepare Information</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Gather your dog's health history, breed information, and any existing DNA test results. 
                  Your veterinarian can help collect samples if needed.
                </p>
                {user ? (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/my-pets')}
                  >
                    View My Dogs
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/register')}
                  >
                    Create Account
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">3</span>
                </div>
                <h3 className="font-heading text-lg mb-2">Submit to NHGRI</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Contact NHGRI through their website or Facebook page to express interest. 
                  They'll guide you through the DNA sample submission process.
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.open(NHGRI_FACEBOOK_URL, '_blank')}
                >
                  Contact via Facebook
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* My Dogs Section (if logged in) */}
        {user && (
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-medium mb-6 flex items-center gap-3">
              <Dog className="w-7 h-7 text-primary" />
              My Dogs
            </h2>
            
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : myDogs.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Dog className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-heading text-xl mb-2">No dogs registered</h3>
                  <p className="text-muted-foreground mb-4">
                    Add your dogs to track their health information and participate in research.
                  </p>
                  <Button onClick={() => navigate('/my-pets')} className="btn-primary">
                    Add Your Dogs
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {myDogs.map((dog) => (
                  <Card key={dog.id} className="card-interactive">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <Avatar className="w-16 h-16">
                          <AvatarImage src={dog.image_url} />
                          <AvatarFallback>
                            <Dog className="w-8 h-8" />
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <h3 className="font-heading text-lg">{dog.name}</h3>
                          <p className="text-sm text-muted-foreground">{dog.breed || 'Mixed breed'}</p>
                          {dog.age_years && (
                            <p className="text-xs text-muted-foreground">
                              {dog.age_years} years old
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="flex-1"
                          onClick={() => navigate(`/genetic-health/${dog.id}`)}
                        >
                          <ClipboardList className="w-4 h-4 mr-1" />
                          Health Checklist
                        </Button>
                        <Button 
                          size="sm"
                          className="flex-1 bg-red-600 hover:bg-red-700"
                          onClick={() => openDogSelection(dog)}
                        >
                          <Dna className="w-4 h-4 mr-1" />
                          Submit DNA
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Genetics Databases Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-medium mb-6 flex items-center gap-3">
            <Dna className="w-7 h-7 text-primary" />
            Canine Genetics Databases & Research Organizations
          </h2>
          
          <p className="text-muted-foreground mb-6">
            These databases and organizations provide comprehensive information about inherited diseases, 
            genetic testing, and ongoing research in canine genetics.
          </p>
          
          <Tabs defaultValue="databases" className="w-full">
            <TabsList className="w-full justify-start mb-4">
              <TabsTrigger value="databases">Databases</TabsTrigger>
              <TabsTrigger value="research">Research Groups</TabsTrigger>
              <TabsTrigger value="testing">DNA Testing</TabsTrigger>
              <TabsTrigger value="organizations">Health Organizations</TabsTrigger>
            </TabsList>
            
            <TabsContent value="databases">
              <DatabasesTab />
            </TabsContent>
            
            <TabsContent value="research">
              <ResearchGroupsTab />
            </TabsContent>
            
            <TabsContent value="testing">
              <DNATestingTab />
            </TabsContent>
            
            <TabsContent value="organizations">
              <OrganizationsTab />
            </TabsContent>
          </Tabs>
        </section>

        {/* AI Breed Health Insights */}
        <section className="mb-12">
          <BreedHealthInsightsPanel />
        </section>

        {/* FAQ Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-medium mb-6 flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-primary" />
            Frequently Asked Questions
          </h2>
          
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="what-is">
              <AccordionTrigger className="font-heading">
                What is the NHGRI Dog Genome Project?
              </AccordionTrigger>
              <AccordionContent>
                The NHGRI Dog Genome Project is a citizen science initiative run by the National Human 
                Genome Research Institute at NIH. It's led by Dr. Elaine Ostrander's Laboratory and 
                focuses on understanding the genetics of health and body structure in domestic dogs. 
                The project relies entirely on voluntary DNA sample submissions from pet dogs.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="why-participate">
              <AccordionTrigger className="font-heading">
                Why should I participate?
              </AccordionTrigger>
              <AccordionContent>
                Your participation helps advance veterinary medicine and can lead to better treatments 
                for genetic diseases in dogs. Because many canine diseases are similar to human disorders, 
                this research also provides valuable insights for human health. Plus, you'll be 
                contributing to a better understanding of your dog's breed.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="how-sample">
              <AccordionTrigger className="font-heading">
                How is the DNA sample collected?
              </AccordionTrigger>
              <AccordionContent>
                DNA samples are typically collected through a simple cheek swab or blood draw performed 
                by your veterinarian. The process is quick, painless, and safe for your dog. NHGRI 
                will provide specific instructions based on the study you're participating in.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="cost">
              <AccordionTrigger className="font-heading">
                Does it cost anything to participate?
              </AccordionTrigger>
              <AccordionContent>
                Participation in the NHGRI Dog Genome Project is typically free. However, if a 
                veterinarian visit is required for sample collection, standard veterinary fees may 
                apply. NHGRI often provides sample collection kits at no cost for approved studies.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="breeds">
              <AccordionTrigger className="font-heading">
                What breeds are they looking for?
              </AccordionTrigger>
              <AccordionContent>
                NHGRI is interested in DNA samples from all types of dogs - purebreds, mixed breeds, 
                and everything in between. Different studies focus on different breeds and conditions. 
                Check their <a href={NHGRI_STUDIES_URL} target="_blank" rel="noopener noreferrer" 
                className="text-primary hover:underline">current studies page</a> to see which breeds 
                are currently being sought.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="privacy">
              <AccordionTrigger className="font-heading">
                How is my information protected?
              </AccordionTrigger>
              <AccordionContent>
                NHGRI follows strict NIH guidelines for data protection and privacy. Your personal 
                information and your dog's identifying details are kept confidential. Research results 
                are published in aggregate form without identifying individual dogs or owners.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>

        {/* Resources Section */}
        <section>
          <h2 className="text-2xl font-heading font-medium mb-6 flex items-center gap-3">
            <Globe className="w-7 h-7 text-primary" />
            Additional Resources
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <a 
              href={NHGRI_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="h-full card-interactive hover:border-primary">
                <CardContent className="p-4 flex items-center gap-3">
                  <Building2 className="w-8 h-8 text-primary" />
                  <div>
                    <p className="font-medium">NHGRI Main Site</p>
                    <p className="text-xs text-muted-foreground">Official project homepage</p>
                  </div>
                </CardContent>
              </Card>
            </a>
            
            <a 
              href={NHGRI_STUDIES_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="h-full card-interactive hover:border-primary">
                <CardContent className="p-4 flex items-center gap-3">
                  <ClipboardList className="w-8 h-8 text-blue-600" />
                  <div>
                    <p className="font-medium">Current Studies</p>
                    <p className="text-xs text-muted-foreground">Active research projects</p>
                  </div>
                </CardContent>
              </Card>
            </a>
            
            <a 
              href="https://research.nhgri.nih.gov/dog_genome/publications/index.shtml" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="h-full card-interactive hover:border-primary">
                <CardContent className="p-4 flex items-center gap-3">
                  <BookOpen className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="font-medium">Publications</p>
                    <p className="text-xs text-muted-foreground">Research findings</p>
                  </div>
                </CardContent>
              </Card>
            </a>
            
            <a 
              href={NHGRI_FACEBOOK_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="h-full card-interactive hover:border-primary">
                <CardContent className="p-4 flex items-center gap-3">
                  <Users className="w-8 h-8 text-purple-600" />
                  <div>
                    <p className="font-medium">Facebook Community</p>
                    <p className="text-xs text-muted-foreground">Connect with researchers</p>
                  </div>
                </CardContent>
              </Card>
            </a>
          </div>
        </section>
      </div>

      {/* DNA Submission Dialog */}
      <Dialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Dna className="w-5 h-5 text-red-600" />
              Submit DNA & Health Information
            </DialogTitle>
            <DialogDescription>
              Express your interest in participating in NHGRI's Dog Genome Project. 
              We'll help you get started.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Dog Selection */}
            {myDogs.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-2 block">Select Dog</label>
                <Select
                  value={dnaForm.dog_id}
                  onValueChange={(val) => {
                    setDnaForm({ ...dnaForm, dog_id: val });
                    setSelectedDog(myDogs.find(d => d.id === val));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a dog" />
                  </SelectTrigger>
                  <SelectContent>
                    {myDogs.map((dog) => (
                      <SelectItem key={dog.id} value={dog.id}>
                        {dog.name} {dog.breed ? `(${dog.breed})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Reason for Submission */}
            <div>
              <label className="text-sm font-medium mb-2 block">Why do you want to participate?</label>
              <Select
                value={dnaForm.reason_for_submission}
                onValueChange={(val) => setDnaForm({ ...dnaForm, reason_for_submission: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="breed_research">Help with breed research</SelectItem>
                  <SelectItem value="health_condition">My dog has a health condition</SelectItem>
                  <SelectItem value="cancer_research">Contribute to cancer research</SelectItem>
                  <SelectItem value="general_interest">General interest in genetics</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Health Conditions */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Known health conditions (optional)
              </label>
              <Textarea
                placeholder="List any diagnosed health conditions, genetic disorders, or concerns..."
                value={dnaForm.health_conditions}
                onChange={(e) => setDnaForm({ ...dnaForm, health_conditions: e.target.value })}
                rows={3}
              />
            </div>

            {/* Existing DNA Test */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dnaForm.has_dna_test}
                  onChange={(e) => setDnaForm({ ...dnaForm, has_dna_test: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">My dog has had a DNA test from another company</span>
              </label>
              
              {dnaForm.has_dna_test && (
                <div className="pl-6 space-y-3">
                  <Input
                    placeholder="DNA testing company (e.g., Embark, Wisdom Panel)"
                    value={dnaForm.dna_test_company}
                    onChange={(e) => setDnaForm({ ...dnaForm, dna_test_company: e.target.value })}
                  />
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={dnaForm.willing_to_share_results}
                      onChange={(e) => setDnaForm({ ...dnaForm, willing_to_share_results: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">I'm willing to share those results with NHGRI</span>
                  </label>
                </div>
              )}
            </div>

            {/* Veterinarian Info */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Veterinarian Information (optional - may be needed for sample collection)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="Veterinarian name"
                  value={dnaForm.veterinarian_name}
                  onChange={(e) => setDnaForm({ ...dnaForm, veterinarian_name: e.target.value })}
                />
                <Input
                  placeholder="Clinic name"
                  value={dnaForm.veterinarian_clinic}
                  onChange={(e) => setDnaForm({ ...dnaForm, veterinarian_clinic: e.target.value })}
                />
              </div>
            </div>

            {/* Additional Notes */}
            <div>
              <label className="text-sm font-medium mb-2 block">Additional notes</label>
              <Textarea
                placeholder="Any other information you'd like to share..."
                value={dnaForm.additional_notes}
                onChange={(e) => setDnaForm({ ...dnaForm, additional_notes: e.target.value })}
                rows={2}
              />
            </div>

            {/* Consent */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Consent & Agreement
              </h4>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dnaForm.consent_research}
                  onChange={(e) => setDnaForm({ ...dnaForm, consent_research: e.target.checked })}
                  className="rounded mt-1"
                />
                <span className="text-sm">
                  I understand this is an expression of interest and that NHGRI will contact me 
                  with further instructions. I consent to my information being shared with the 
                  NHGRI Dog Genome Project for research purposes. *
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dnaForm.consent_contact}
                  onChange={(e) => setDnaForm({ ...dnaForm, consent_contact: e.target.checked })}
                  className="rounded mt-1"
                />
                <span className="text-sm">
                  NHGRI researchers may contact me about participating in studies
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setSubmitDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={handleSubmitDNA}
                disabled={submitting || !dnaForm.consent_research}
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Submit & Visit NHGRI
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DogHealth;
